import { TableWrapperStack } from '@common/components';
import { EntityIcon } from '@common/Entity/EntityIcons';
import { SnowflakePwdAuthBanner } from '@common/Fabric/SnowflakePwdAuthBanner';
import { ProjectHeader } from '@common/Header/PageHeader';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Stack, StackItem, Text, MetadataPageContainer, OverlaySpinner } from '@prophecy/ui-v3';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import React, { useEffect, useMemo } from 'react';

import { useMixPanel } from '../../../context/mixpanel/context';
import { QueryKey, StatusMapKeys, Transpiler_Headline } from '../config';
import { Error } from '../Import/utils';
import { useAllTranspiledData } from '../useAllTranspiledData';
import { useInfoCardData } from '../useInfoCardData';
import { EntityTabs } from './components/EntityTabs';
import { InfoHeader } from './components/InfoHeader';
import { RightInfoPanel } from './components/RightInfoPanel';

export const ImportedInfo = ({
  isBulk,
  id,
  version,
  type
}: {
  isBulk: boolean;
  version: string;
  id: string;
  type: string;
}) => {
  const { data, isError, isLoading } = useInfoCardData(id, isBulk);

  const { data: allData, isError: hasOverViewError, isLoading: isOverviewLoading } = useAllTranspiledData(id, isBulk);

  const overviewData = useMemo(() => allData?.data || { entities: [] }, [allData?.data]);

  const totalPipelines = overviewData.entities.filter(
    (row) => isCaseInsensitiveEqual(row.entity, Entity.Pipeline) || isCaseInsensitiveEqual(row.entity, Entity.Model)
  );
  const totalJobs = overviewData.entities.filter((row) => isCaseInsensitiveEqual(row.entity, Entity.Job));

  const transpiledJobsCount = totalJobs.filter(
    (row) =>
      isCaseInsensitiveEqual(row.status, StatusMapKeys.imported) ||
      isCaseInsensitiveEqual(row.status, StatusMapKeys.completed)
  ).length;

  const transpiledPipelinesCount = totalPipelines.filter(
    (row) =>
      isCaseInsensitiveEqual(row.status, StatusMapKeys.imported) ||
      isCaseInsensitiveEqual(row.status, StatusMapKeys.completed)
  ).length;
  const infoData = data?.data;
  const isSql = infoData?.language === BackEndCodeLanguage.sql;

  const overallSummaryUI = (
    <Stack gap={theme.spaces.x6} width='200px'>
      <Text tone={theme.colors.gray700} level='sm'>
        Summary
      </Text>
      {totalJobs.length > 0 && (
        <Stack direction='horizontal' alignY='center' align='space-between' width='100%'>
          <Text tone={theme.colors.gray600} weight={theme.fontWeight.medium} level='xs'>
            Plans Transpiled
          </Text>
          <Text tone={theme.colors.gray600} level='xs'>
            {transpiledJobsCount} out of {totalJobs.length}
          </Text>
        </Stack>
      )}
      <Stack direction='horizontal' alignY='center' align='space-between' width='100%'>
        <Text tone={theme.colors.gray600} weight={theme.fontWeight.medium} level='xs'>
          Graphs Transpiled
        </Text>
        <Text tone={theme.colors.gray600} level='xs'>
          {transpiledPipelinesCount} out of {totalPipelines.length}
        </Text>
      </Stack>
    </Stack>
  );

  const { track } = useMixPanel();
  useEffect(() => {
    track(EntityEvents.transpile.home.entityOpen, {
      transpilerId: id,
      entity: QueryKey,
      version
    });
  }, [track, id, version]);

  if (isError && !infoData) {
    return <Error message='Some error occurred' />;
  }

  const name = infoData?.name || 'Detail';
  const header = (
    <ProjectHeader
      crumbs={[
        {
          icon: <EntityIcon size='m' entity={Entity.Transpiler} />,
          elementType: 'link',
          children: Transpiler_Headline,
          to: Private_Routes.Transpiler.getUrl(),
          'data-test-id': 'imported-info-transpiler-crumb'
        },
        {
          children: name,
          elementType: 'link',
          to: Private_Routes.TranspilerDetail.getUrl({ uid: id, version, type }),
          'data-test-id': 'imported-info-transpiler-detail-crumb'
        }
      ]}
    />
  );

  const content = (
    <Stack gap={theme.spaces.x48} width='100%' height='100%'>
      <DocumentTitle title={`Import:${name}`} />
      {isLoading && <OverlaySpinner spinning={true} />}
      <InfoHeader infoData={infoData} isBulk={isBulk} transpilerId={id} />
      <StackItem grow='1'>
        <TableWrapperStack
          width='100%'
          height='100%'
          direction='horizontal'
          align='space-between'
          gap={theme.spaces.x64}
          padding={`0 0 ${theme.spaces.x40}`}>
          <StackItem grow='1'>
            <EntityTabs
              hasOverViewError={hasOverViewError}
              isOverviewLoading={isOverviewLoading}
              overviewData={overviewData}
              isBulk={isBulk}
              isSql={isSql}
              id={id}
              projectId={infoData?.projectId}
              type={type}
              projectName={infoData?.projectName}
            />
          </StackItem>
          <Stack width='250px' height='100%'>
            <RightInfoPanel infoData={infoData} overallSummaryUI={overallSummaryUI} />
          </Stack>
        </TableWrapperStack>
      </StackItem>
    </Stack>
  );

  return (
    <MetadataPageContainer banner={<SnowflakePwdAuthBanner />} main={content} header={header}></MetadataPageContainer>
  );
};
